import { HelpCircle } from 'lucide-react';
import { type JSX } from 'react';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useLanguage } from '@/lib/contexts/LanguageContext';

interface OtherInfoPageProps {
  formData: {
    additionalInfo: string;
    deliveryEmail: string;
    termsAccepted: boolean;
    privacyAccepted: boolean;
    dataComplete: boolean;
    submitConfirmation: boolean;
  };
  onChange: (section: string, field: string, value: string | boolean) => void;
}

export default function OtherInfoPage({
  formData,
  onChange,
}: Readonly<OtherInfoPageProps>): JSX.Element {
  const { t } = useLanguage();

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-semibold dark:text-white">{t.otherInfo}</h2>
      <p className="text-gray-500 dark:text-gray-400">{t.reviewPrompt}</p>

      <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-blue-800 dark:bg-blue-950">
        <p className="text-sm text-blue-800 dark:text-blue-200">{t.pdfPricingInfo}</p>
      </div>

      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="additionalInfo">{t.otherInfoTextLabel}</Label>
          <Textarea
            id="additionalInfo"
            value={formData.additionalInfo}
            onChange={(e) => onChange('', 'additionalInfo', e.target.value)}
            placeholder={t.otherInfoTextPlaceholder}
            className="min-h-32 w-full"
          />
          <p className="text-xs text-gray-500 dark:text-gray-400">{t.otherInfoTextHelp}</p>
        </div>
      </div>

      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="deliveryEmail" className="flex items-center">
            {t.deliveryEmailLabel} <span className="ml-1 text-red-500">*</span>
          </Label>
          <Input
            id="deliveryEmail"
            type="email"
            value={formData.deliveryEmail}
            onChange={(e) => onChange('', 'deliveryEmail', e.target.value)}
            placeholder={t.deliveryEmailPlaceholder}
            required
            className="w-full"
          />
          <p className="text-xs text-gray-500 dark:text-gray-400">{t.deliveryEmailHelp}</p>
        </div>
      </div>

      <div className="space-y-4 pt-4">
        <h3 className="text-lg font-medium dark:text-white">{t.legalAgreements}</h3>

        <div className="flex items-start space-x-3">
          <Checkbox
            id="termsAccepted"
            checked={formData.termsAccepted || false}
            onCheckedChange={(checked) => onChange('', 'termsAccepted', Boolean(checked))}
          />
          <div className="flex-1 space-y-1">
            <div className="flex items-center gap-2">
              <Label htmlFor="termsAccepted" className="cursor-pointer text-sm font-medium">
                {t.acceptTerms}
              </Label>
              <Dialog>
                <DialogTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-auto p-1 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                  >
                    <HelpCircle className="h-4 w-4" />
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-white dark:bg-gray-900">
                  <DialogHeader>
                    <DialogTitle>{t.termsDialogTitle}</DialogTitle>
                    <DialogDescription className="text-left">
                      {t.termsDialogContent}
                    </DialogDescription>
                  </DialogHeader>
                </DialogContent>
              </Dialog>
            </div>
            <p className="text-xs text-gray-500">{t.termsHelp}</p>
          </div>
        </div>

        <div className="flex items-start space-x-3">
          <Checkbox
            id="privacyAccepted"
            checked={formData.privacyAccepted || false}
            onCheckedChange={(checked) => onChange('', 'privacyAccepted', Boolean(checked))}
          />
          <div className="space-y-1">
            <Label htmlFor="privacyAccepted" className="cursor-pointer text-sm font-medium">
              {t.acceptPrivacy}
            </Label>
            <p className="text-xs text-gray-500">{t.privacyHelp}</p>
          </div>
        </div>

        <div className="flex items-start space-x-3">
          <Checkbox
            id="dataComplete"
            checked={formData.dataComplete || false}
            onCheckedChange={(checked) => onChange('', 'dataComplete', Boolean(checked))}
          />
          <div className="space-y-1">
            <Label htmlFor="dataComplete" className="cursor-pointer text-sm font-medium">
              {t.confirmComplete}
            </Label>
            <p className="text-xs text-gray-500">{t.completeHelp}</p>
          </div>
        </div>

        <div className="flex items-start space-x-3 border-t border-gray-200 pt-4 dark:border-gray-800">
          <Checkbox
            id="submitConfirmation"
            checked={formData.submitConfirmation || false}
            onCheckedChange={(checked) => {
              console.warn('Submit confirmation checkbox changed:', checked);
              onChange('', 'submitConfirmation', Boolean(checked));
            }}
          />
          <div className="space-y-1">
            <Label htmlFor="submitConfirmation" className="cursor-pointer text-sm font-medium">
              {t.readyToSubmit}
            </Label>
            <p className="text-xs text-gray-500">{t.submitHelp}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
